import React from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';

const createStyles = (colors, isBlurBackground, rounded, stretch, height) => StyleSheet.create({
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: isBlurBackground ? `${colors.solid_color_tertiary}44` : colors.solid_color_tertiary,
    padding: 4,
    borderRadius: rounded ? 30 : 8,
    borderWidth: Platform.OS === 'web' ? 1 : StyleSheet.hairlineWidth,
    borderColor: colors.border_color_secondary,
    ...(stretch && { width: '100%', alignSelf: 'stretch' }),
  },
  toggleButton: {
    paddingHorizontal: 20,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: rounded ? 20 : 6,
  },
  toggleButtonStretched: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  toggleButtonActive: {
    backgroundColor: colors.solid_color_secondary,
  },
  badge: {
    position: 'absolute',
    top: -18,
    right: -25,
    backgroundColor: colors.accent_color_primary,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
});

const ToggleButton = React.memo(({ 
  isActive, 
  onPress, 
  text, 
  badge,
  styles,
  stretched,
}) => {
  return (
    <Pressable
      style={[
        styles.toggleButton,
        stretched && styles.toggleButtonStretched,
        isActive && styles.toggleButtonActive,
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      aria-pressed={isActive}
      accessibilityLabel={`Select ${text}`}
    >
      <Text 
        size="n"
        color={isActive ? 'primary' : 'secondary'}
        weight={isActive ? 'medium' : 'normal'}
      >
        {text}
      </Text>
      {badge && (
        <View style={styles.badge}>
          <Text size="xs" weight="bold" color="primary">{badge}</Text>
        </View>
      )}
    </Pressable>
  );
});

ToggleButton.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onPress: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  badge: PropTypes.string,
  styles: PropTypes.object.isRequired,
  stretched: PropTypes.bool,
};

ToggleButton.displayName = 'ToggleButton';

const Toggle = ({ 
  options, 
  value, 
  onChange, 
  colors,
  containerStyle,
  isBlurBackground = false,
  rounded = true,
  stretch = false,
  height,
}) => {
  const styles = React.useMemo(
    () => createStyles(colors, isBlurBackground, rounded, stretch, height),
    [colors, isBlurBackground, rounded, stretch, height],
  );

  return (
    <View style={[
      styles.toggleContainer,
      isBlurBackground && Platform.OS === 'web' && {
        backdropFilter: 'blur(18px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.8)',
      },
      containerStyle,
    ]}>
      {options.map((option, index) => (
        <ToggleButton 
          key={`toggle-button-${index}`}
          isActive={value === option.value}
          onPress={() => onChange(option.value)}
          text={option.label}
          badge={option.badge}
          styles={styles}
          stretched={stretch}
        />
      ))}
    </View>
  );
};

Toggle.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired,
    badge: PropTypes.string,
  })).isRequired,
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
  containerStyle: PropTypes.object,
  isBlurBackground: PropTypes.bool,
  rounded: PropTypes.bool,
  stretch: PropTypes.bool,
};

export default React.memo(Toggle);
